<script setup lang="ts">
// NPM
import { ref } from 'vue';
import { useMq } from 'vue3-mq';

// Components
import {
  BaseCard,
  BaseEmptyState,
  Modal,
  BaseButton,
  BaseBadge,
} from '@magma-app/magma-lapilli';

// API
import { useChallenges } from '@/api/challenges/use-challenges';
import { useAddProof } from '@/api/challenges/use-add-proof';
import { useActionDeepLink } from '@/composables/useActionDeepLink';
import { reactive } from 'vue';

// Plugins
const mq = useMq();

const { data: challenges, isLoading } = useChallenges();
const { mutate: addProof } = useAddProof();

const modal = ref<number | null>(null);

// Opens one action's modal directly from a ?challengeId= deep link.
useActionDeepLink({
  type: 'challenge',
  bucket: 'rejected',
  onOpen: (_item, id) => {
    modal.value = id;
  },
});

const getChallengeImgUrl = (challenge: { pictureUrl?: string | null }) =>
  challenge.pictureUrl
    ? challenge.pictureUrl
    : mq.lgPlus
      ? '/img/challenge_lg.png'
      : '/img/challenge.png';
</script>

<template>
  <div
    v-if="!isLoading"
    class="block h-full flex-1 px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap': challenges?.rejected.length
    }"
  >
    <template v-if="challenges?.rejected.length">
      <Modal
        v-for="challenge in challenges?.rejected"
        :key="challenge.userChallengeId"
        :open="modal === challenge.challengeId"
        content-class="max-w-[90dvw] sm:max-w-[480px]"
        @open-focus="(e) => e.preventDefault()"
        @open-change="
          (open) => {
            modal = challenge.challengeId;

            if (!open) {
              modal = null;
            }
          }
        "
      >
        <template #trigger>
          <BaseCard
            aria-hidden="true"
            class="mb-2 lg:mb-6 lg:mr-6"
            :title="challenge.name"
            :description="challenge.description"
            :img-url="getChallengeImgUrl(challenge)"
            :points="challenge.points"
            :badge-status="{ points: challenge.points, type: 'challenge' }"
            :display-points="!challenge.pictureUrl"
          />
        </template>
        <template #content>
          <div>
            <img
              :src="getChallengeImgUrl(challenge)"
              :alt="challenge.name"
              class="h-[200px] w-full rounded-[5px] object-cover mb-5"
            />
            <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
              {{ challenge.name }}
            </h3>
            <BaseBadge
              class="mt-2"
              size="md"
              :text="challenge.points"
              icon-type="icon"
              icon-name="star"
            />
            <div
              class="rich-text-content text-mgm-txt-sm text-foreground-default my-5 max-h-[40dvh] lg:max-h-auto overflow-y-auto"
              v-html="challenge.description"
            ></div>
            <BaseButton
              size="lg"
              disabled
              class="!bg-background-danger-default w-full !text-foreground-danger-default border-none"
              >{{ $t('challenges.rejected.cta') }}</BaseButton
            >
          </div>
        </template>
      </Modal>
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('challengesView.empty.all')"
      type="empty-list"
    />
  </div>
</template>
